// A canonical PNG: the same bytes for the same pixels in every implementation. Fill fingerprints hash the pictures of image fields as
// PNG files, so the encoder must not vary: 8-bit RGBA, filter 0 on every row, one IDAT chunk holding a zlib stream of stored
// (uncompressed) blocks, no other chunks. The Rust Reader (rd_pdf::fill::canonical_png) writes exactly the same bytes.

const CRC = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
    return t;
})();

export function crc32(data: Uint8Array, seed = 0): number {
    let c = ~seed >>> 0;
    for (let i = 0; i < data.length; i++) c = CRC[(c ^ data[i]!) & 255]! ^ (c >>> 8);
    return ~c >>> 0;
}

export function adler32(data: Uint8Array): number {
    let a = 1, b = 0;
    for (let i = 0; i < data.length; i++) { a = (a + data[i]!) % 65521; b = (b + a) % 65521; }
    return ((b << 16) | a) >>> 0;
}

const be32 = (n: number) => [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255];

function chunk(type: string, data: Uint8Array): Uint8Array {
    const out = new Uint8Array(12 + data.length);
    out.set(be32(data.length), 0);
    for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
    out.set(data, 8);
    out.set(be32(crc32(out.subarray(4, 8 + data.length))), 8 + data.length);
    return out;
}

/** `rgba` is straight (not premultiplied) 8-bit RGBA, top row first. */
export function canonicalPng(width: number, height: number, rgba: Uint8Array): Uint8Array {
    if (rgba.length !== width * height * 4) throw new Error("The pixel data does not match the picture size.");
    const raw = new Uint8Array(height * (1 + width * 4));
    for (let y = 0; y < height; y++) {
        raw[y * (1 + width * 4)] = 0;
        raw.set(rgba.subarray(y * width * 4, (y + 1) * width * 4), y * (1 + width * 4) + 1);
    }
    const blocks = Math.max(1, Math.ceil(raw.length / 65535));
    const z = new Uint8Array(2 + raw.length + blocks * 5 + 4);
    z[0] = 0x78; z[1] = 0x01;
    let p = 2;
    for (let b = 0; b < blocks; b++) {
        const from = b * 65535, len = Math.min(65535, raw.length - from);
        z[p++] = b === blocks - 1 ? 1 : 0;
        z[p++] = len & 255; z[p++] = len >> 8; z[p++] = ~len & 255; z[p++] = (~len >> 8) & 255;
        z.set(raw.subarray(from, from + len), p);
        p += len;
    }
    z.set(be32(adler32(raw)), p);
    const ihdr = new Uint8Array([...be32(width), ...be32(height), 8, 6, 0, 0, 0]);
    const parts = [new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]), chunk("IHDR", ihdr), chunk("IDAT", z), chunk("IEND", new Uint8Array(0))];
    const out = new Uint8Array(parts.reduce((s, x) => s + x.length, 0));
    let o = 0;
    for (const x of parts) { out.set(x, o); o += x.length; }
    return out;
}
